import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import Search from 'components/shared/Search';
import SegmentedControl from 'components/shared/SegmentedControl';
import ControlledTable from 'components/shared/table/ControlledTable';
import { useProfile } from 'context/profile';
import { useDebounce } from 'hooks/useDebounce';
import usePagination from 'hooks/usePagination';
import { FacilityNetworkSummary, PayerNetworkSummary } from 'models/GroupPayerNetworkStatus';
import { PERMISSIONS } from 'models/Profile';
import {
  groupPayerNetworkStatusQueryKeys,
  indexFacilityNetworkSummaries,
  indexPayerNetworkSummaries,
} from 'services/api/groupPayerNetworkStatuses';
import { serializeSort } from 'stores/helpers';
import { BodySmall, H2 } from 'styles/typography';
import PlusIcon from 'svg/PlusIcon';

import AddPayerNetworksModal from './AddPayerNetworksModal';
import { facilityColumns, payerColumns } from './facilityNetworkStatusColumns';
import FacilityNetworkStatusEmptyState from './FacilityNetworkStatusEmptyState';
import RemoveAllMappingsModal, { RemoveAllMappingsTarget } from './RemoveAllMappingsModal';

type View = 'facilities' | 'payers';

const VIEW_OPTIONS = [
  { label: 'By Facility', value: 'facilities' },
  { label: 'By Payer', value: 'payers' },
];

const DEFAULT_SORT: SortingState = [{ id: 'name', desc: false }];

export default function FacilityNetworkStatusPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view: View = searchParams.get('view') === 'payers' ? 'payers' : 'facilities';

  // The view lives in the URL so Back from a payer's page returns to By Payer. Replaced, not pushed,
  // so toggling views doesn't fill the history. Keyed so each view starts unsearched on page 1.
  const handleViewChange = (value: string) =>
    setSearchParams(value === 'payers' ? { view: 'payers' } : {}, { replace: true });

  return <NetworkStatusLanding key={view} view={view} onViewChange={handleViewChange} />;
}

function NetworkStatusLanding({ view, onViewChange }: { view: View; onViewChange: (value: string) => void }) {
  const { profile } = useProfile();
  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>(DEFAULT_SORT);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput);
  const [removing, setRemoving] = useState<RemoveAllMappingsTarget | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const queryParams = { ...pagination.paging, sortBy: serializeSort(sort), search };

  const facilities = useQuery({
    queryKey: groupPayerNetworkStatusQueryKeys.facilities(queryParams),
    queryFn: ({ signal }) => indexFacilityNetworkSummaries(queryParams, signal),
    enabled: view === 'facilities',
  });

  const payers = useQuery({
    queryKey: groupPayerNetworkStatusQueryKeys.payers(queryParams),
    queryFn: ({ signal }) => indexPayerNetworkSummaries(queryParams, signal),
    enabled: view === 'payers',
  });

  const activeQuery = view === 'facilities' ? facilities : payers;

  // Removing all mappings for a payer drops it from By Payer, which can empty the last page.
  const totalPages = activeQuery.data?.meta.totalPages;
  useEffect(() => {
    if (totalPages === undefined) return;
    const lastPage = Math.max(totalPages, 1);
    if (pagination.paging.page > lastPage) pagination.setPageIndex(lastPage - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  const facilityTableColumns = useMemo(() => facilityColumns(setRemoving), []);
  const payerTableColumns = useMemo(() => payerColumns(setRemoving), []);

  const sorting = { sortBy: sort, setSortBy: setSort };
  const showEmptyState = activeQuery.data?.meta.totalRecords === 0 && !search;

  return (
    <Page>
      <Page.Header>
        <HeaderRow>
          <TitleGroup>
            <Title>Facility Network Status</Title>
            <SearchContainer>
              <Search
                value={searchInput}
                onChange={setSearchInput}
                placeholder={view === 'facilities' ? 'Search Facility' : 'Search Payer'}
              />
            </SearchContainer>
            <SegmentedControl options={VIEW_OPTIONS} selectedValue={view} onChange={onViewChange} />
          </TitleGroup>
          <AccessGuard accessor={PERMISSIONS.groupPayerNetworkStatusEdit}>
            <Button variant='primary' onClick={() => setAdding(true)}>
              <PlusIcon width={16} height={16} />
              Add Payers
            </Button>
          </AccessGuard>
        </HeaderRow>
      </Page.Header>

      <Page.Content>
        <Intro>
          Manage the in and out-of-network status for each of your facilities below. This status will be displayed under
          the Facility Decision step per facility when viewing an Inbound Referral in Olio.
        </Intro>

        {showEmptyState && view === 'facilities' && (
          <FacilityNetworkStatusEmptyState
            title='No Facilities Yet'
            message={`There are currently no facilities added for ${profile.actingClient.name} in Olio yet. Once facilities are added, they will be displayed here.`}
          />
        )}

        {showEmptyState && view === 'payers' && (
          <FacilityNetworkStatusEmptyState
            title='No Payers Added'
            message='Add payers to set a network status for your facilities'>
            <AccessGuard accessor={PERMISSIONS.groupPayerNetworkStatusEdit}>
              <EmptyStateAddButton variant='primary' onClick={() => setAdding(true)}>
                Add Payers
              </EmptyStateAddButton>
            </AccessGuard>
          </FacilityNetworkStatusEmptyState>
        )}

        {view === 'facilities' && !showEmptyState && (
          <ControlledTable<FacilityNetworkSummary>
            data={facilities.data?.data ?? []}
            columns={facilityTableColumns}
            loading={facilities.isLoading}
            pageCount={facilities.data?.meta.totalPages ?? 1}
            pagination={pagination}
            sorting={sorting}
            enableSortingRemoval={false}
          />
        )}

        {view === 'payers' && !showEmptyState && (
          <ControlledTable<PayerNetworkSummary>
            data={payers.data?.data ?? []}
            columns={payerTableColumns}
            loading={payers.isLoading}
            pageCount={payers.data?.meta.totalPages ?? 1}
            pagination={pagination}
            sorting={sorting}
            enableSortingRemoval={false}
          />
        )}
      </Page.Content>

      {removing && <RemoveAllMappingsModal target={removing} onClose={() => setRemoving(null)} />}
      {adding && <AddPayerNetworksModal variant={{ kind: 'payers' }} onClose={() => setAdding(false)} />}
    </Page>
  );
}

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchContainer = styled.div`
  width: 200px;
`;

export const Title = styled(H2)`
  font-size: 20px;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
`;

const EmptyStateAddButton = styled(Button)`
  margin-top: 8px;
`;

const Intro = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;
